import { FC, ReactNode, useEffect } from 'react';
import styled, { useTheme } from 'styled-components';
import { useClickAwayListener } from '@algoan/dashboard/utils';
import { CrossIcon } from '../shared-components/icons';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  title: string;
}

export const Drawer: FC<DrawerProps> = ({ children, open, onClose, title }: DrawerProps) => {
  const theme = useTheme();
  const contentRef = useClickAwayListener<HTMLDivElement>(onClose);

  useEffect(() => {
    // when the drawer is open, it is not possible to scroll "background" page
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [open]);

  return (
    <S.Container visible={open}>
      <S.Background visible={open} />
      <S.Content ref={contentRef} visible={open}>
        <S.CloseContainer onClick={onClose}>
          <S.CloseIcon color={theme.secondary.softBlue2} size="large" />
        </S.CloseContainer>
        <S.Header>
          <S.Title>{title}</S.Title>
        </S.Header>
        <S.Children>{children}</S.Children>
      </S.Content>
    </S.Container>
  );
};

const transition = (property: string): string => `transition: ${property} 0.3s linear;`;

const S = {
  Container: styled.div<{ visible: boolean }>`
    position: fixed;
    height: 100vh;
    width: 100%;
    left: 0;
    top: 0;
    z-index: 10000;
    visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
    ${transition('visibility')}
  `,
  Background: styled.div<{ visible: boolean }>`
    position: absolute;
    height: 100%;
    width: 100%;
    background: rgba(0, 0, 0, 0.75);
    opacity: ${({ visible }) => (visible ? '1' : '0')};
    ${transition('opacity')}
  `,
  Content: styled.div<{ visible: boolean }>`
    overflow: auto;
    display: flex;
    flex-direction: column;
    position: absolute;
    height: 100%;
    top: 0;
    min-width: 250px;
    background: #ffffff;
    right: ${({ visible }) => (visible ? '0' : '-100%')};
    ${transition('right')}
  `,
  Header: styled.div`
    padding: 56px 26px 8px 26px;
  `,
  Children: styled.div`
    padding: 32px 26px;
    flex: 1;
  `,
  Title: styled.h3`
    color: ${({ theme }) => theme.algoanBlue};
    font-weight: bold;
    font-size: 20px;
    line-height: 25px;
    margin: 0;
  `,
  CloseContainer: styled.div`
    cursor: pointer;
  `,
  CloseIcon: styled(CrossIcon)`
    position: absolute;
    top: 24px;
    right: 24px;
    width: 16px;
    height: 16px;
  `,
};
